// src/pages/DocumentDetail.tsx
import React, {useCallback, useEffect, useState} from 'react';
import { Modal, Button, notification, Typography, Divider, List, Card, Form } from 'antd';
import { BarChartOutlined, DiffOutlined, CheckCircleOutlined, EditOutlined, SaveOutlined } from '@ant-design/icons';
import { useParams } from 'react-router-dom';
import Spinner from "../components/common/Spinner";
import { deleteDocument, fetchVerificationView, patchDocumentDetails, verifyDocument } from "../redux/api/documents/documentsThunks";
import { fetchDocumentDetails } from "../redux/api/documents/documentsThunks";
import { patchPropertyDetails } from "../redux/api/documents/propertiesThunks";
import router from "../router";
import { PropertyFull, VerificationView } from "../types";
import { useAppDispatch } from "../redux/hooks";
import EditPropertyAction from "../components/property/actions/EditPropertyAction";
import DeletePropertyAction from "../components/property/actions/DeletePropertyAction";
import GroupedPropertiesList from "../components/property/GroupedPropertiesList";
import {useDocumentReady} from "../hooks/useDocumentReady";
import StatusBadge from '../components/common/StatusBadge';
import { getEtlStatus, getVerificationStatus } from '../services/utils/metadata-helpers';
import DynamicDetailForm from '../components/common/DynamicDetailForm';
import { renderDetail } from '../utils/detailRendererRegistry';

const { Title, Text } = Typography;

const DocumentDetailPage: React.FC = () => {
    const { documentId } = useParams();
    const dispatch = useAppDispatch();

    const [loading, setLoading] = useState<boolean>(false);
    const [verificationData, setVerificationData] = useState<VerificationView | null>(null);
    const [isEditDocDetails, setIsEditDocDetails] = useState(false);
    const [editingPropertyId, setEditingPropertyId] = useState<string | null>(null);
    const [docForm] = Form.useForm();
    const [propForm] = Form.useForm();

    const { document, isLoading, error } = useDocumentReady(documentId ?? '');

    useEffect(() => {
        if (!documentId) {
            return;
        }
        if (document?.detailsLoaded) {
            return;
        }
        dispatch(fetchDocumentDetails(documentId)).catch(() => {
            // Error state is surfaced via documents slice and useDocumentReady.
        });
    }, [dispatch, document?.detailsLoaded, documentId]);

    const handleDeleteDocument = useCallback(() => {
        if (!documentId) {
            notification.error({
                message: 'Неверный ID документа',
                description: 'Параметр документа имеет некорректное значение.',
            });
            return;
        }
        Modal.confirm({
            title: 'Вы уверены, что хотите удалить этот документ?',
            okText: 'Удалить',
            cancelText: 'Нет',
            onOk: async () => {
                setLoading(true);
                try {
                    const action = await dispatch(deleteDocument(documentId));
                    if (deleteDocument.fulfilled.match(action)) {
                        notification.success({ message: 'Документ удален' });
                        await router.navigate('/documents');
                    } else {
                        notification.error({
                            message: 'Ошибка при удалении документа',
                            description: action.payload || 'Не удалось удалить документ.',
                        });
                    }
                } catch (err: any) {
                    notification.error({
                        message: 'Ошибка при удалении документа',
                        description: err.message || 'Не удалось удалить документ.',
                    });
                } finally {
                    setLoading(false);
                }
            },
        });
    }, [documentId, dispatch]);

    const handleAnalyticsButton = useCallback(async (id: string) => {
        await router.navigate(`/analytics/${id}`);
    }, []);

    const handleComparisonButton = useCallback(async (id: string) => {
        await router.navigate(`/comparison/${id}`);
    }, []);

    const handleVerificationButton = useCallback(async () => {
        if (documentId) {
            setLoading(true);
            try {
                const action = await dispatch(fetchVerificationView(documentId));
                if (fetchVerificationView.fulfilled.match(action)) {
                    setVerificationData(action.payload);
                    notification.success({ message: 'Режим верификации активирован' });
                }
            } catch (e) {
                notification.error({ message: 'Ошибка при загрузке данных верификации' });
            } finally {
                setLoading(false);
            }
        }
    }, [documentId, dispatch]);

    const handleFinalVerify = useCallback(async () => {
        if (documentId) {
            setLoading(true);
            try {
                await dispatch(verifyDocument(documentId)).unwrap();
                notification.success({ message: 'Документ успешно верифицирован' });
                setVerificationData(null);
            } catch (e: any) {
                notification.error({ message: 'Ошибка верификации', description: e.message });
            } finally {
                setLoading(false);
            }
        }
    }, [documentId, dispatch]);

    const handleSaveDocDetails = useCallback(async (values: any) => {
        if (documentId) {
            const detailsToPatch = Object.entries(values).map(([code, value]) => ({ code, value }));
            try {
                await dispatch(patchDocumentDetails({ documentId, details: detailsToPatch })).unwrap();
                notification.success({ message: 'Метаданные документа обновлены' });
                setIsEditDocDetails(false);
            } catch (e: any) {
                notification.error({ message: 'Ошибка обновления', description: e.message });
            }
        }
    }, [documentId, dispatch]);

    const handleSavePropertyDetails = useCallback(async (propertyId: string, values: any) => {
        const detailsToPatch = Object.entries(values).map(([code, value]) => ({ code, value }));
        try {
            await dispatch(patchPropertyDetails({ propertyId, details: detailsToPatch })).unwrap();
            notification.success({ message: 'Детали свойства обновлены' });
            setEditingPropertyId(null);
        } catch (e: any) {
            notification.error({ message: 'Ошибка обновления', description: e.message });
        }
    }, [dispatch]);

    const propertyActions = useCallback((property: PropertyFull, _isSelected: boolean) => {
        const actions = [
            <EditPropertyAction key="edit" property={property} />,
            <DeletePropertyAction key="delete" property={property} />,
        ];

        if (verificationData) {
            actions.push(
                <Button 
                    key="edit-details" 
                    type="link" 
                    icon={<EditOutlined />} 
                    onClick={() => setEditingPropertyId(property.id)}
                >
                    Детали
                </Button>
            );
        }

        return actions;
    }, [verificationData]);

    if (error) {
        return (
            <div className="p-4">
                <h2 className="text-xl text-red-600">Ошибка: {error}</h2>
            </div>
        );
    }
    if (isLoading || loading) {
        return <Spinner />;
    }

    if (!document) {
        return (
            <div className="p-4">
                <h2 className="text-xl">Документ не найден</h2>
            </div>
        );
    }

    const allProperties: PropertyFull[] = document.properties || [];

    if (!document.detailsLoaded || loading) {
        return <Spinner />;
    }

    return (
        <div className="p-4">
            <div className="flex justify-between items-start mb-4 bg-white shadow p-6 rounded">
                <div className="flex-col flex-1 mr-4">
                    <div className="flex items-center space-x-3 mb-2">
                        <Title level={2} style={{ margin: 0 }}>{document.title}</Title>
                        <StatusBadge type="etl" status={getEtlStatus(document)} />
                        <StatusBadge type="verification" status={getVerificationStatus(document)} />
                    </div>
                    {document.description && (
                        <Text type="secondary" className="block mb-4">{document.description}</Text>
                    )}
                    
                    {document.details && document.details.length > 0 && (
                        <div className="mt-4">
                            <div className="flex justify-between items-center">
                                <Divider orientation="left" style={{ margin: '12px 0', flex: 1 }}>Метаданные</Divider>
                                {verificationData && !isEditDocDetails && (
                                    <Button type="link" icon={<EditOutlined />} onClick={() => setIsEditDocDetails(true)}>
                                        Редактировать
                                    </Button>
                                )}
                            </div>
                            
                            {isEditDocDetails && verificationData ? (
                                <Card size="small" className="mb-4">
                                    <DynamicDetailForm 
                                        definitions={verificationData.schema.documentDetails}
                                        initialValues={document.details.reduce((acc, d) => ({ ...acc, [d.code]: d.value }), {})}
                                        onFinish={handleSaveDocDetails}
                                        formInstance={docForm}
                                    />
                                    <div className="flex justify-end space-x-2 mt-2">
                                        <Button size="small" onClick={() => setIsEditDocDetails(false)}>Отмена</Button>
                                        <Button 
                                            size="small" 
                                            type="primary" 
                                            icon={<SaveOutlined />}
                                            onClick={() => docForm.submit()}
                                        >
                                            Сохранить
                                        </Button>
                                    </div>
                                </Card>
                            ) : (
                                <List
                                    grid={{ gutter: 16, xs: 1, sm: 2, md: 3 }}
                                    dataSource={document.details.filter(d =>
                                        !['etl_status', 'verification_status'].includes(d.code) &&
                                        !d.code.startsWith('etl_warning_')
                                    )}
                                    renderItem={detail => (
                                        <List.Item style={{ marginBottom: 8 }}>
                                            <div>
                                                <Text strong>{detail.name}: </Text>
                                                {renderDetail(detail)}
                                            </div>
                                        </List.Item>
                                    )}
                                />
                            )}
                        </div>
                    )}
                </div>
                <div className="flex flex-col space-y-2 min-w-[200px]">
                    {verificationData ? (
                        <Button
                            type="primary"
                            danger
                            icon={<CheckCircleOutlined />}
                            onClick={handleFinalVerify}
                            disabled={loading}
                        >
                            Завершить верификацию
                        </Button>
                    ) : (
                        <Button
                            type="primary"
                            icon={<CheckCircleOutlined />}
                            onClick={handleVerificationButton}
                            disabled={loading}
                        >
                            Верификация
                        </Button>
                    )}
                    <Button
                        icon={<BarChartOutlined />}
                        onClick={() => handleAnalyticsButton(document.id)}
                        disabled={loading}
                    >
                        Аналитика
                    </Button>
                    <Button
                        icon={<DiffOutlined />}
                        onClick={() => handleComparisonButton(document.id)}
                        disabled={loading}
                    >
                        Сравнить
                    </Button>
                    <Divider style={{ margin: '8px 0' }} />
                    <Button danger onClick={handleDeleteDocument} disabled={loading}>
                        Удалить документ
                    </Button>
                </div>
            </div>

            <GroupedPropertiesList
                properties={allProperties}
                selectedProperty={undefined}
                actions={propertyActions}
                showNextButton={true}
            />

            <Modal
                title="Детали свойства"
                open={!!editingPropertyId}
                onCancel={() => setEditingPropertyId(null)}
                footer={null}
                width={600}
            >
                {editingPropertyId && verificationData && (
                    <DynamicDetailForm 
                        definitions={verificationData.schema.properties}
                        initialValues={
                            allProperties.find(p => p.id === editingPropertyId)?.details.reduce((acc, d) => ({ ...acc, [d.code]: d.value }), {}) || {}
                        }
                        onFinish={(values) => handleSavePropertyDetails(editingPropertyId, values)}
                        formInstance={propForm}
                    />
                )}
                <div className="flex justify-end mt-4">
                    <Button onClick={() => setEditingPropertyId(null)} style={{ marginRight: 8 }}>Отмена</Button>
                    <Button type="primary" onClick={() => propForm.submit()}>Сохранить</Button>
                </div>
            </Modal>
        </div>
    );
};

export default React.memo(DocumentDetailPage);
